import { useEffect, useMemo, useState } from 'react';
import {
  BarChart3, Sparkles, Play, Download, Plus, X, Loader2, Table2, AlertCircle,
  Building2, Users, Route, Layers, Phone, Target, Calendar, ChevronRight,
} from 'lucide-react';
import {
  reportsService, type ReportSchema, type ReportSpec, type RunResult, type FilterOp, type MonthlySummary, type SummaryDrill,
} from '../services/reportsService';

const OPS: { value: FilterOp; label: string; noValue?: boolean }[] = [
  { value: 'eq', label: '=' }, { value: 'neq', label: '≠' },
  { value: 'contains', label: 'contains' },
  { value: 'gt', label: '>' }, { value: 'gte', label: '≥' },
  { value: 'lt', label: '<' }, { value: 'lte', label: '≤' },
  { value: 'in', label: 'in (a,b,c)' },
  { value: 'isnull', label: 'is empty', noValue: true },
  { value: 'notnull', label: 'is not empty', noValue: true },
];

const EXAMPLES = [
  'All tyre-industry accounts in India with maturity L2',
  'Count of accounts by industry',
  'Partner accounts grouped by partner type',
  'Contacts with engagement score above 50',
  'Accounts with no plant type set',
  'Prospects in Egypt with ICP score over 60',
];

// One-click standard reports. columns:[] lets the backend use each entity's
// default columns; groupBy produces a count breakdown, otherwise the total count
// shows in the results header.
type Template = { title: string; desc: string; icon: any; spec: ReportSpec };
const TEMPLATES: Template[] = [
  { title: 'Total Accounts', desc: 'Every account on record', icon: Building2,
    spec: { entity: 'accounts', columns: [], filters: [], limit: 5000, title: 'All Accounts' } },
  { title: 'Total Contacts', desc: 'Every contact on record', icon: Users,
    spec: { entity: 'contacts', columns: [], filters: [], limit: 5000, title: 'All Contacts' } },
  { title: 'Accounts by Industry', desc: 'Count grouped by industry', icon: BarChart3,
    spec: { entity: 'accounts', columns: [], filters: [], groupBy: 'industryVertical', limit: 1000, title: 'Accounts by Industry' } },
  { title: 'Accounts by Region', desc: 'Count grouped by region', icon: Target,
    spec: { entity: 'accounts', columns: [], filters: [], groupBy: 'region', limit: 1000, title: 'Accounts by Region' } },
  { title: 'Buyer vs Partner', desc: 'Accounts by type', icon: Layers,
    spec: { entity: 'accounts', columns: [], filters: [], groupBy: 'accountType', limit: 1000, title: 'Buyer vs Partner Accounts' } },
  { title: 'Journeys by Stage', desc: 'Progress across 14 stages', icon: Route,
    spec: { entity: 'journeys', columns: [], filters: [], groupBy: 'currentStage', limit: 1000, title: 'Journeys by Stage' } },
  { title: 'Journeys by Status', desc: 'Active / completed split', icon: Route,
    spec: { entity: 'journeys', columns: [], filters: [], groupBy: 'status', limit: 1000, title: 'Journeys by Status' } },
  { title: 'Contacts by Status', desc: 'Cold / engaged / active', icon: Users,
    spec: { entity: 'contacts', columns: [], filters: [], groupBy: 'status', limit: 1000, title: 'Contacts by Status' } },
  { title: 'MQL Submissions', desc: 'All MQLs submitted', icon: Target,
    spec: { entity: 'mqls', columns: [], filters: [], limit: 5000, title: 'MQL Submissions' } },
  { title: 'AI Calls by Status', desc: 'Call outcomes', icon: Phone,
    spec: { entity: 'ai_calls', columns: [], filters: [], groupBy: 'status', limit: 1000, title: 'AI Calls by Status' } },
];

const fmt = (v: any) => {
  if (v === null || v === undefined || v === '') return '—';
  if (Array.isArray(v)) return v.join(', ');
  if (typeof v === 'object') return JSON.stringify(v);
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) {
    const d = new Date(s);
    // Show time too when it isn't midnight (events/calls carry a meaningful time).
    return d.getHours() || d.getMinutes()
      ? d.toLocaleString([], { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
      : d.toLocaleDateString();
  }
  return s;
};

function MiniTable({ title, rows }: { title: string; rows: { key: string; count: number }[] }) {
  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <div className="px-3 py-2 bg-muted/30 text-xs font-semibold">{title}</div>
      {rows.length === 0 ? (
        <p className="px-3 py-3 text-xs text-muted-foreground">No data this month.</p>
      ) : (
        <table className="w-full text-xs">
          <tbody>
            {rows.map(r => (
              <tr key={r.key} className="border-t border-border/60">
                <td className="px-3 py-1.5">{r.key}</td>
                <td className="px-3 py-1.5 text-right font-medium">{r.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function RegionTable({ rows }: { rows: { key: string; total: number; buyer: number; partner: number }[] }) {
  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <div className="px-3 py-2 bg-muted/30 text-xs font-semibold">Accounts added by region</div>
      {rows.length === 0 ? (
        <p className="px-3 py-3 text-xs text-muted-foreground">No data this month.</p>
      ) : (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-muted-foreground">
              <th className="px-3 py-1.5 text-left font-medium">Region</th>
              <th className="px-3 py-1.5 text-right font-medium">Total</th>
              <th className="px-3 py-1.5 text-right font-medium">Buyer</th>
              <th className="px-3 py-1.5 text-right font-medium">Partner</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.key} className="border-t border-border/60">
                <td className="px-3 py-1.5">{r.key}</td>
                <td className="px-3 py-1.5 text-right font-semibold">{r.total}</td>
                <td className="px-3 py-1.5 text-right">{r.buyer}</td>
                <td className="px-3 py-1.5 text-right">{r.partner}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function EngagementTable({ title, label, rows }: { title: string; label: string; rows: { key: string; email: number; call: number }[] }) {
  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <div className="px-3 py-2 bg-muted/30 text-xs font-semibold">{title}</div>
      {rows.length === 0 ? (
        <p className="px-3 py-3 text-xs text-muted-foreground">No engagement this period.</p>
      ) : (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-muted-foreground">
              <th className="px-3 py-1.5 text-left font-medium">{label}</th>
              <th className="px-3 py-1.5 text-right font-medium">Email</th>
              <th className="px-3 py-1.5 text-right font-medium">Call</th>
              <th className="px-3 py-1.5 text-right font-medium">Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.key} className="border-t border-border/60">
                <td className="px-3 py-1.5">{r.key}</td>
                <td className="px-3 py-1.5 text-right">{r.email}</td>
                <td className="px-3 py-1.5 text-right">{r.call}</td>
                <td className="px-3 py-1.5 text-right font-semibold">{r.email + r.call}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function ReportsPage() {
  const [schema, setSchema] = useState<ReportSchema>({});
  const [prompt, setPrompt] = useState('');
  const [spec, setSpec] = useState<ReportSpec | null>(null);
  const [result, setResult] = useState<RunResult | null>(null);
  const [generating, setGenerating] = useState(false);
  const [running, setRunning] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<'day' | 'week' | 'month' | 'quarter' | 'year'>('month');
  const [refDate, setRefDate] = useState(new Date().toISOString().slice(0, 10));
  const [summary, setSummary] = useState<MonthlySummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [drill, setDrill] = useState<{ metric: string; label: string } | null>(null);
  const [drillData, setDrillData] = useState<SummaryDrill | null>(null);
  const [drillLoading, setDrillLoading] = useState(false);

  const openDrill = async (metricKey: string, metricLabel: string) => {
    setDrill({ metric: metricKey, label: metricLabel });
    setDrillData(null);
    setDrillLoading(true);
    try {
      setDrillData(await reportsService.getSummaryDrill(metricKey, period, refDate));
    } catch {
      setDrillData(null);
    } finally {
      setDrillLoading(false);
    }
  };

  useEffect(() => {
    setSummaryLoading(true);
    reportsService.getPeriodSummary(period, refDate)
      .then(setSummary).catch(() => setSummary(null))
      .finally(() => setSummaryLoading(false));
  }, [period, refDate]);

  useEffect(() => {
    reportsService.getSchema()
      .then(s => {
        setSchema(s);
        const first = Object.keys(s)[0];
        if (first) setSpec({ entity: first, columns: s[first].defaultColumns, filters: [], limit: 1000, title: '' });
      })
      .catch(e => setError(e?.response?.data?.error || 'Failed to load report schema'));
  }, []);

  const entity = spec?.entity || '';
  const entityMeta = schema[entity];
  const fieldKeys = useMemo(() => (entityMeta ? Object.keys(entityMeta.fields) : []), [entityMeta]);

  const patch = (p: Partial<ReportSpec>) => setSpec(s => (s ? { ...s, ...p } : s));

  const changeEntity = (e: string) => {
    setResult(null);
    setSpec({ entity: e, columns: schema[e]?.defaultColumns || [], filters: [], groupBy: null, limit: 1000, title: '' });
  };

  const toggleColumn = (f: string) => {
    if (!spec) return;
    const has = spec.columns.includes(f);
    patch({ columns: has ? spec.columns.filter(c => c !== f) : [...spec.columns, f] });
  };

  const generate = async () => {
    if (!prompt.trim()) return;
    setGenerating(true); setError(null); setResult(null);
    try {
      const { spec: s } = await reportsService.generate(prompt);
      setSpec(s);
      const r = await reportsService.run(s);
      setResult(r);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.response?.data?.error || 'Could not build a report from that prompt');
    } finally {
      setGenerating(false);
    }
  };

  const run = async () => {
    if (!spec) return;
    setRunning(true); setError(null);
    try {
      setResult(await reportsService.run(spec));
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Report failed');
    } finally {
      setRunning(false);
    }
  };

  const exportCsv = async () => {
    if (!spec) return;
    setExporting(true);
    try { await reportsService.exportCsv(spec); }
    catch { setError('Export failed'); }
    finally { setExporting(false); }
  };

  const runTemplate = async (t: Template) => {
    setError(null); setResult(null); setPrompt('');
    const s: ReportSpec = { ...t.spec };
    setSpec(s);
    setRunning(true);
    try { setResult(await reportsService.run(s)); }
    catch (e: any) { setError(e?.response?.data?.message || 'Report failed'); }
    finally { setRunning(false); }
  };

  const exportSummary = () => {
    if (!summary) return;
    const esc = (s: any) => { const t = String(s ?? ''); return /[",\n\r]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t; };
    const lines: string[] = [];
    lines.push(`${esc(summary.period.charAt(0).toUpperCase() + summary.period.slice(1))} Summary,${esc(summary.label)}`);
    lines.push('');
    lines.push('Metric,This period,Total');
    summary.metrics.forEach(m => lines.push(`${esc(m.label)},${m.value},${m.total ?? ''}`));
    lines.push('');
    lines.push('Accounts added by industry (this month),Count');
    summary.breakdowns.accountsByIndustry.forEach(b => lines.push(`${esc(b.key)},${b.count}`));
    lines.push('');
    lines.push('Accounts added by region (this month)');
    lines.push('Region,Total,Buyer,Partner');
    summary.breakdowns.accountsByRegion.forEach(b => lines.push(`${esc(b.key)},${b.total},${b.buyer},${b.partner}`));
    if (summary.breakdowns.engagementByIndustry?.length) {
      lines.push('');
      lines.push('Engagement by industry,Email,Call,Total');
      summary.breakdowns.engagementByIndustry.forEach(b => lines.push(`${esc(b.key)},${b.email},${b.call},${b.email + b.call}`));
    }
    if (summary.breakdowns.engagementByRegion?.length) {
      lines.push('');
      lines.push('Engagement by region,Email,Call,Total');
      summary.breakdowns.engagementByRegion.forEach(b => lines.push(`${esc(b.key)},${b.email},${b.call},${b.email + b.call}`));
    }
    const csv = '\uFEFF' + lines.join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url; a.download = `${summary.period}_summary_${summary.label.replace(/[^a-z0-9]+/gi, '_').toLowerCase()}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <BarChart3 className="h-6 w-6 text-primary" /> Reports
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Describe the report you want in plain English, or build it manually. Runs read-only across accounts, contacts, journeys and prospects.
        </p>
      </div>

      {/* Monthly summary dashboard */}
      <div className="rounded-xl border border-border p-4 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-primary" />
            <h2 className="text-sm font-semibold">Activity summary</h2>
            {summary && <span className="text-xs text-muted-foreground">· {summary.label}</span>}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex rounded-lg border border-border overflow-hidden">
              {(['day', 'week', 'month', 'quarter', 'year'] as const).map(p => (
                <button key={p} onClick={() => setPeriod(p)}
                  className={`text-xs px-2.5 py-1.5 capitalize ${period === p ? 'bg-primary text-primary-foreground font-medium' : 'hover:bg-muted'}`}>
                  {p}
                </button>
              ))}
            </div>
            <input type="date" value={refDate} onChange={e => setRefDate(e.target.value)}
              className="text-sm px-2 py-1.5 rounded-md border border-border bg-background" />
            <button onClick={exportSummary} disabled={!summary}
              className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border hover:border-primary/40 disabled:opacity-50">
              <Download className="h-3.5 w-3.5" /> Export
            </button>
          </div>
        </div>

        {summaryLoading ? (
          <div className="flex justify-center py-6"><Loader2 className="h-4 w-4 animate-spin text-primary" /></div>
        ) : summary ? (
          <>
            {(() => {
              const core = summary.metrics.filter(m => !m.group);
              const groups = summary.metrics.filter(m => m.group);
              const byGroup = groups.reduce((acc, m) => { (acc[m.group!] ||= []).push(m); return acc; }, {} as Record<string, typeof summary.metrics>);
              const Card = (m: typeof summary.metrics[number]) => (
                <button key={m.key} onClick={() => openDrill(m.key, m.label)}
                  className="text-left rounded-lg border border-border p-3 hover:border-primary/50 hover:shadow-sm transition-all group relative">
                  <p className="text-2xl font-bold leading-none">{m.value}</p>
                  <p className="text-xs font-medium mt-1.5 flex items-center gap-0.5">{m.label}
                    <ChevronRight className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </p>
                  {m.total != null && <p className="text-[10px] text-muted-foreground">of {m.total} total</p>}
                </button>
              );
              return (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">{core.map(Card)}</div>
                  {Object.entries(byGroup).map(([g, ms]) => (
                    <div key={g}>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">{g}</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">{ms.map(Card)}</div>
                    </div>
                  ))}
                </div>
              );
            })()}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <MiniTable title="Accounts added by industry" rows={summary.breakdowns.accountsByIndustry} />
              <RegionTable rows={summary.breakdowns.accountsByRegion} />
            </div>
            {(summary.breakdowns.engagementByIndustry?.length || summary.breakdowns.engagementByRegion?.length) ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <EngagementTable title="Engagement by industry" label="Industry" rows={summary.breakdowns.engagementByIndustry || []} />
                <EngagementTable title="Engagement by region" label="Region" rows={summary.breakdowns.engagementByRegion || []} />
              </div>
            ) : null}
          </>
        ) : (
          <p className="text-sm text-muted-foreground">No summary available for this month.</p>
        )}
      </div>

      {/* Standard report templates — one-click */}
      <div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Standard reports</p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
          {TEMPLATES.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.title} onClick={() => runTemplate(t)} disabled={running}
                className="text-left p-3 rounded-xl border border-border bg-card hover:border-primary/40 hover:bg-primary/5 transition-colors disabled:opacity-50">
                <Icon className="h-4 w-4 text-primary mb-1.5" />
                <p className="text-xs font-semibold leading-tight">{t.title}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5 leading-tight">{t.desc}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Prompt box */}
      <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-3">
        <label className="text-sm font-semibold flex items-center gap-1.5">
          <Sparkles className="h-4 w-4 text-primary" /> Ask for a report
        </label>
        <div className="flex gap-2">
          <textarea
            value={prompt}
            onChange={e => setPrompt(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) generate(); }}
            rows={2}
            placeholder="e.g. Count of buyer accounts by industry in India, highest first"
            className="flex-1 px-3 py-2 rounded-lg border bg-background text-sm resize-none"
          />
          <button
            onClick={generate}
            disabled={generating || !prompt.trim()}
            className="px-4 rounded-lg bg-primary text-primary-foreground font-semibold text-sm flex items-center gap-2 disabled:opacity-50"
          >
            {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            Generate
          </button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {EXAMPLES.map(ex => (
            <button key={ex} onClick={() => setPrompt(ex)}
              className="text-[11px] px-2 py-1 rounded-full border border-border bg-background hover:border-primary/40 text-muted-foreground">
              {ex}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/20 p-3 text-sm text-red-700 dark:text-red-400">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /> <span>{error}</span>
        </div>
      )}

      {/* Builder */}
      {spec && entityMeta && (
        <div className="rounded-xl border border-border p-4 space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Data</label>
              <select value={entity} onChange={e => changeEntity(e.target.value)}
                className="px-3 py-2 border rounded-md text-sm bg-background">
                {Object.entries(schema).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Group by (count)</label>
              <select value={spec.groupBy || ''} onChange={e => patch({ groupBy: e.target.value || null })}
                className="px-3 py-2 border rounded-md text-sm bg-background">
                <option value="">— none (list rows) —</option>
                {fieldKeys.filter(f => entityMeta.fields[f].type !== 'date').map(f => <option key={f} value={f}>{entityMeta.fields[f].label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Row limit</label>
              <input type="number" value={spec.limit || 1000} min={1} max={5000}
                onChange={e => patch({ limit: parseInt(e.target.value) || 1000 })}
                className="w-24 px-3 py-2 border rounded-md text-sm bg-background" />
            </div>
            <div className="flex-1" />
            <button onClick={run} disabled={running}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground font-semibold text-sm flex items-center gap-2 disabled:opacity-50">
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />} Run
            </button>
            <button onClick={exportCsv} disabled={exporting || !result}
              className="px-4 py-2 rounded-lg border border-border font-semibold text-sm flex items-center gap-2 disabled:opacity-50">
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} CSV
            </button>
          </div>

          {/* Columns (only for list mode) */}
          {!spec.groupBy && (
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1.5">Columns</label>
              <div className="flex flex-wrap gap-1.5">
                {fieldKeys.map(f => {
                  const on = spec.columns.includes(f);
                  return (
                    <button key={f} onClick={() => toggleColumn(f)}
                      className={`text-xs px-2 py-1 rounded-full border ${on ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground'}`}>
                      {entityMeta.fields[f].label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Filters */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-medium text-muted-foreground">Filters</label>
              <button
                onClick={() => patch({ filters: [...spec.filters, { field: fieldKeys[0], op: 'eq', value: '' }] })}
                className="text-xs flex items-center gap-1 text-primary">
                <Plus className="h-3 w-3" /> Add filter
              </button>
            </div>
            <div className="space-y-2">
              {spec.filters.length === 0 && <p className="text-xs text-muted-foreground">No filters — all rows.</p>}
              {spec.filters.map((f, i) => {
                const meta = entityMeta.fields[f.field];
                const opDef = OPS.find(o => o.value === f.op);
                return (
                  <div key={i} className="flex items-center gap-2">
                    <select value={f.field} onChange={e => {
                      const filters = [...spec.filters]; filters[i] = { ...f, field: e.target.value }; patch({ filters });
                    }} className="px-2 py-1.5 border rounded-md text-sm bg-background">
                      {fieldKeys.map(k => <option key={k} value={k}>{entityMeta.fields[k].label}</option>)}
                    </select>
                    <select value={f.op} onChange={e => {
                      const filters = [...spec.filters]; filters[i] = { ...f, op: e.target.value as FilterOp }; patch({ filters });
                    }} className="px-2 py-1.5 border rounded-md text-sm bg-background">
                      {OPS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    {!opDef?.noValue && (
                      meta?.values ? (
                        <select value={f.value ?? ''} onChange={e => {
                          const filters = [...spec.filters]; filters[i] = { ...f, value: e.target.value }; patch({ filters });
                        }} className="px-2 py-1.5 border rounded-md text-sm bg-background flex-1">
                          <option value="">—</option>
                          {meta.values.map(v => <option key={v} value={v}>{v}</option>)}
                        </select>
                      ) : (
                        <input value={f.value ?? ''} placeholder="value"
                          onChange={e => { const filters = [...spec.filters]; filters[i] = { ...f, value: e.target.value }; patch({ filters }); }}
                          className="px-2 py-1.5 border rounded-md text-sm bg-background flex-1" />
                      )
                    )}
                    <button onClick={() => patch({ filters: spec.filters.filter((_, x) => x !== i) })}
                      className="text-muted-foreground hover:text-red-500"><X className="h-4 w-4" /></button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="rounded-xl border border-border overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b bg-muted/30">
            <div className="flex items-center gap-2 text-sm font-semibold">
              {result.mode === 'grouped' ? <BarChart3 className="h-4 w-4" /> : <Table2 className="h-4 w-4" />}
              {spec?.title || 'Results'}
            </div>
            <div className="text-xs text-muted-foreground">
              {result.mode === 'grouped'
                ? `${result.rows.length} groups`
                : `Showing ${result.returned ?? result.rows.length} of ${result.total}`}
            </div>
          </div>
          {result.warnings && result.warnings.length > 0 && (
            <div className="px-4 py-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/20 border-b border-amber-200/60">
              {result.warnings.join(' · ')}
            </div>
          )}
          <div className="overflow-x-auto max-h-[60vh]">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 sticky top-0">
                <tr>
                  {result.columns.map(c => (
                    <th key={c} className="text-left font-medium px-3 py-2 whitespace-nowrap capitalize">
                      {entityMeta?.fields[c]?.label || c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.rows.map((r, i) => (
                  <tr key={i} className="border-t border-border/60 hover:bg-muted/20">
                    {result.columns.map(c => (
                      <td key={c} className="px-3 py-1.5 whitespace-nowrap">{fmt(r[c])}</td>
                    ))}
                  </tr>
                ))}
                {result.rows.length === 0 && (
                  <tr><td colSpan={result.columns.length} className="px-3 py-6 text-center text-muted-foreground">No matching rows.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Metric drill-down slide-over ── */}
      {drill && (
        <div className="fixed inset-0 z-50 flex justify-end" onClick={() => setDrill(null)}>
          <div className="absolute inset-0 bg-black/30" />
          <div className="relative w-full max-w-2xl bg-background border-l border-border shadow-xl h-full flex flex-col animate-in slide-in-from-right"
            onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <div>
                <p className="text-sm font-semibold">{drill.label}</p>
                <p className="text-xs text-muted-foreground">
                  {drillData ? `${drillData.total} record${drillData.total === 1 ? '' : 's'} · ${drillData.label}` : summary?.label}
                </p>
              </div>
              <div className="flex items-center gap-2">
                {drillData && drillData.rows.length > 0 && (
                  <button
                    onClick={() => {
                      const cols = drillData.columns;
                      const esc = (s: any) => { const v = s == null ? '' : String(s); return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v; };
                      const csv = '\uFEFF' + [cols.join(','), ...drillData.rows.map(r => cols.map(c => esc(fmt(r[c]))).join(','))].join('\r\n');
                      const url = window.URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
                      const a = document.createElement('a'); a.href = url;
                      a.download = `${drill.metric}_${period}.csv`; document.body.appendChild(a); a.click(); a.remove();
                      window.URL.revokeObjectURL(url);
                    }}
                    className="text-xs flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-border hover:border-primary/40">
                    <Download className="h-3.5 w-3.5" /> Export
                  </button>
                )}
                <button onClick={() => setDrill(null)} className="p-1.5 rounded-lg hover:bg-muted"><X className="h-4 w-4" /></button>
              </div>
            </div>
            <div className="flex-1 overflow-auto">
              {drillLoading ? (
                <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
              ) : !drillData || drillData.rows.length === 0 ? (
                <p className="px-5 py-10 text-center text-sm text-muted-foreground">No records for this metric in the selected period.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-muted/40 sticky top-0">
                    <tr>{drillData.columns.map(c => (
                      <th key={c} className="text-left font-medium px-4 py-2 whitespace-nowrap capitalize">{c}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {drillData.rows.map((r, i) => (
                      <tr key={i} className="border-t border-border/60 hover:bg-muted/20 align-top">
                        {drillData.columns.map(c => (
                          <td key={c} className={`px-4 py-2 ${c === 'outcome' ? 'max-w-xs' : 'whitespace-nowrap'}`}>{fmt(r[c])}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
